import { z } from 'zod';
import { AUTOMATION_DECISIONS, type AutomationRunStatus, ENROLLMENT_STATUSES, type AutomationDecision, type EmailStatus, type EnrollmentStatus } from '../constants';
import { type IsoDateTime, NullableText, RequiredText } from './common';

const csvOrArray = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v), z.array(item));

/** Longest delay a step may wait (two years) — longer gaps are almost certainly typos. */
export const MAX_STEP_DELAY_DAYS = 730;
export const MAX_SEQUENCE_STEPS = 20;

const days = z.number().int().min(0).max(3650);

export const SequenceStepInput = z.object({
  templateId: z.uuid('Pick a template'),
  delayDays: z.number().int().min(0, 'Delay cannot be negative').max(MAX_STEP_DELAY_DAYS),
});
export type SequenceStepInput = z.infer<typeof SequenceStepInput>;

const SequenceFields = {
  name: RequiredText(200),
  description: NullableText(2000).optional(),
  autoEnroll: z.boolean(),
  requireApproval: z.boolean(),
  /** Empty = any relationship that does not block outreach. */
  relationshipKeys: z.array(z.string().min(1).max(60)).max(50),
  /** Empty = any open stage. */
  stageKeys: z.array(z.string().min(1).max(60)).max(50),
  minDaysSinceMeaningful: days.nullable(),
  minDaysSinceLastContact: days.nullable(),
  excludeOpenOpportunities: z.boolean(),
  minScore: z.number().int().min(0).max(100).nullable(),
  includeNeverContacted: z.boolean(),
  reenrollCooldownDays: days,
  steps: z.array(SequenceStepInput).min(1, 'Add at least one step').max(MAX_SEQUENCE_STEPS),
};

export const CreateSequenceBody = z.object({
  ...SequenceFields,
  autoEnroll: SequenceFields.autoEnroll.default(false),
  requireApproval: SequenceFields.requireApproval.default(true),
  relationshipKeys: SequenceFields.relationshipKeys.default([]),
  stageKeys: SequenceFields.stageKeys.default([]),
  minDaysSinceMeaningful: SequenceFields.minDaysSinceMeaningful.default(null),
  minDaysSinceLastContact: SequenceFields.minDaysSinceLastContact.default(null),
  excludeOpenOpportunities: SequenceFields.excludeOpenOpportunities.default(true),
  minScore: SequenceFields.minScore.default(null),
  includeNeverContacted: SequenceFields.includeNeverContacted.default(false),
  reenrollCooldownDays: SequenceFields.reenrollCooldownDays.default(90),
});
export type CreateSequenceBody = z.input<typeof CreateSequenceBody>;

/** Partial update; `steps`, when present, replaces every step atomically. */
export const UpdateSequenceBody = z.object({
  name: SequenceFields.name.optional(),
  description: SequenceFields.description,
  autoEnroll: SequenceFields.autoEnroll.optional(),
  requireApproval: SequenceFields.requireApproval.optional(),
  relationshipKeys: SequenceFields.relationshipKeys.optional(),
  stageKeys: SequenceFields.stageKeys.optional(),
  minDaysSinceMeaningful: SequenceFields.minDaysSinceMeaningful.optional(),
  minDaysSinceLastContact: SequenceFields.minDaysSinceLastContact.optional(),
  excludeOpenOpportunities: SequenceFields.excludeOpenOpportunities.optional(),
  minScore: SequenceFields.minScore.optional(),
  includeNeverContacted: SequenceFields.includeNeverContacted.optional(),
  reenrollCooldownDays: SequenceFields.reenrollCooldownDays.optional(),
  steps: SequenceFields.steps.optional(),
  version: z.number().int().min(1),
});
export type UpdateSequenceBody = z.input<typeof UpdateSequenceBody>;

export const SequenceEnrollmentsQuery = z.object({
  status: csvOrArray(z.enum(ENROLLMENT_STATUSES)).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type SequenceEnrollmentsQuery = z.input<typeof SequenceEnrollmentsQuery>;

export const AutomationDecisionsQuery = z.object({
  sequenceId: z.uuid().optional(),
  businessId: z.uuid().optional(),
  decision: csvOrArray(z.enum(AUTOMATION_DECISIONS)).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type AutomationDecisionsQuery = z.input<typeof AutomationDecisionsQuery>;

export const AutomationRunsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export type AutomationRunsQuery = z.input<typeof AutomationRunsQuery>;

/** Manual enrollment. Without `contactId` the recipient is picked like auto-enrollment (primary, then decision maker). */
export const EnrollLeadBody = z.object({
  sequenceId: z.uuid('Pick a sequence'),
  contactId: z.uuid().nullish(),
});
export type EnrollLeadBody = z.input<typeof EnrollLeadBody>;

export const EnrollmentCheckQuery = z.object({
  sequenceId: z.uuid(),
  contactId: z.uuid().optional(),
});
export type EnrollmentCheckQuery = z.input<typeof EnrollmentCheckQuery>;

export const EnrollmentActionBody = z.object({
  reason: NullableText(500).optional(),
});
export type EnrollmentActionBody = z.input<typeof EnrollmentActionBody>;

// ---------------------------------------------------------------------------------------------------------
// DTOs

export type SequenceStepDTO = {
  id: string;
  stepOrder: number;
  delayDays: number;
  templateId: string;
  templateName: string;
  templateSubject: string;
  /** False when the template was deactivated or deleted — the step waits until it is fixed. */
  templateAvailable: boolean;
};

export type SequenceSummaryDTO = {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  autoEnroll: boolean;
  requireApproval: boolean;
  relationshipKeys: string[];
  stageKeys: string[];
  minDaysSinceMeaningful: number | null;
  minDaysSinceLastContact: number | null;
  excludeOpenOpportunities: boolean;
  minScore: number | null;
  includeNeverContacted: boolean;
  reenrollCooldownDays: number;
  stepCount: number;
  activeEnrollments: number;
  pausedEnrollments: number;
  /** Emails generated by this sequence in the last 30 days (any status except cancelled). */
  emailsQueued30d: number;
  version: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
};

export type SequenceDetailDTO = SequenceSummaryDTO & {
  steps: SequenceStepDTO[];
  createdBy: string | null;
  updatedBy: string | null;
};

export type EnrollmentDTO = {
  id: string;
  sequenceId: string;
  sequenceName: string;
  sequenceActive: boolean;
  businessId: string;
  businessName: string;
  businessCity: string | null;
  contactId: string;
  contactName: string | null;
  contactEmail: string | null;
  status: EnrollmentStatus;
  /** stepOrder of the next step to send. */
  currentStep: number;
  totalSteps: number;
  nextRunAt: IsoDateTime | null;
  enrolledAt: IsoDateTime;
  /** Null when enrolled automatically. */
  enrolledBy: string | null;
  endedAt: IsoDateTime | null;
  statusReason: string | null;
  lastEmailId: string | null;
  lastEmailStatus: EmailStatus | null;
};

export type AutomationDecisionDTO = {
  id: string;
  runId: string | null;
  createdAt: IsoDateTime;
  decision: AutomationDecision;
  reasonCode: string;
  reason: string;
  businessId: string;
  businessName: string;
  sequenceId: string | null;
  sequenceName: string | null;
  enrollmentId: string | null;
  contactId: string | null;
  contactName: string | null;
  emailId: string | null;
};

export type AutomationRunDTO = {
  id: string;
  trigger: 'schedule' | 'manual';
  status: AutomationRunStatus;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  enrolledCount: number;
  queuedCount: number;
  skippedCount: number;
  pausedCount: number;
  stoppedCount: number;
  /** Failure message, or why the run was skipped (kill switch, compliance, provider). */
  error: string | null;
  triggeredBy: string | null;
};

export type AutomationStatusDTO = {
  enabled: boolean;
  runIntervalMinutes: number;
  maxEnrollmentsPerRun: number;
  emailProviderConfigured: boolean;
  /** Everything preventing automation from running right now (empty = ready). */
  blockers: string[];
  lastRun: AutomationRunDTO | null;
  /** True while a manual "Run now" job is queued or running. */
  runQueued: boolean;
};

export type SequencePreviewLeadDTO = {
  businessId: string;
  name: string;
  city: string | null;
  relationshipKey: string;
  stageKey: string;
  score: number;
  tierKey: string;
  lastMeaningfulAt: IsoDateTime | null;
  lastContactedAt: IsoDateTime | null;
  contactName: string | null;
  contactEmail: string | null;
  eligible: boolean;
  code: string;
  /** Human explanation without the "Enrolled:/Skipped:" prefix, e.g. "warm lead, no meaningful interaction for 34 days". */
  reason: string;
};

export type SequencePreviewDTO = {
  /** Leads matching the audience criteria right now (before recipient checks). */
  matching: number;
  /** How many of the matching leads were checked for a sendable recipient (capped for speed). */
  evaluated: number;
  /** Of the evaluated leads, how many would be enrolled on the next run. */
  eligible: number;
  sample: SequencePreviewLeadDTO[];
};

export type LeadEnrollmentDTO = EnrollmentDTO & {
  nextStep: { stepOrder: number; templateName: string; delayDays: number } | null;
};

export type LeadAutomationDTO = {
  liveEnrollment: LeadEnrollmentDTO | null;
  history: EnrollmentDTO[];
  decisions: AutomationDecisionDTO[];
  /** Whether the current user may enroll / pause / resume / stop on this lead. */
  canEdit: boolean;
};

export type EnrollmentCheckDTO = {
  eligible: boolean;
  code: string;
  reason: string;
  /** Non-blocking notes: frequency cap delays, global kill switch off, approval required… */
  warnings: string[];
  contact: { id: string; name: string | null; email: string | null } | null;
};

export type RunAutomationResponse = { queued: boolean };
